import { notification } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { getApiError } from "tm-shared/api";

import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { QueueTaskStatus, useAsyncTaskQueue } from "@/hooks/useAsyncTaskQueue";
import {
    deleteRequestQueueItem,
    patchRequestQueueItem,
    setRequestQueueRunning,
    stopRequestQueue,
    touchRequestQueue,
} from "@/store/slices/offline";
import { addFailedQueueItem } from "@/store/slices/ui";
import { setTaskCompleted } from "@/store/slices/work";
import {
    QueueCommandMap,
    QueueCommandType,
    RequestQueueItem,
} from "@/types/requestQueue";
import { isOnline } from "@/utils/network/isOnline";

import { createErrorMarker } from "./useRequestQueue.utils";
import { useRequestQueueCommands } from "./useRequestQueueCommands";

const MoveToFailed = createErrorMarker("MOVE_TO_FAILED");
const StopQueue = createErrorMarker("STOP_QUEUE");

const MAX_ALLOWED_LATENCY_MS = 2000;

const displayStopWarning = () => {
    notification.warning({
        message: "Черга тимчасово зупинена",
        description:
            "Мережа відповідає занадто повільно, тому ми зупинили відправку, щоб уникнути помилок. Відновіть інтернет і повторіть спробу.",
    });
};

const callHandler = <T extends QueueCommandType>(
    handlers: {
        [K in QueueCommandType]: (p: QueueCommandMap[K]) => Promise<unknown>;
    },
    type: T,
    params: QueueCommandMap[T],
) => handlers[type](params);

export const useRequestQueue = () => {
    const dispatch = useAppDispatch();

    const handlers = useRequestQueueCommands();
    const {
        requestQueue,
        offlineMode,
        isInitialDataLoading,
        requestQueueStatus,
    } = useAppSelector((s) => s.offline);

    const offlineModeRef = useRef(offlineMode);
    useEffect(() => {
        offlineModeRef.current = offlineMode;
    }, [offlineMode]);

    const [currentItem, setCurrentItem] = useState<RequestQueueItem | null>(
        null,
    );

    const processingRef = useRef(false);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const { queue, progress } = useAsyncTaskQueue();

    const setProcessing = useCallback(
        (value: boolean) => {
            processingRef.current = value;
            dispatch(setRequestQueueRunning(value));

            if (!value) setCurrentItem(null);
        },
        [dispatch],
    );

    useEffect(() => {
        if (!currentItem) return;

        dispatch(touchRequestQueue());
        dispatch(
            patchRequestQueueItem({
                id: currentItem.id,
                patch: { progress },
            }),
        );
    }, [progress, currentItem, dispatch]);

    const { lastTickAt, isRunning } = requestQueueStatus;

    useEffect(() => {
        const TIMEOUT = 3 * 60 * 1000; // 3 хвилини

        const intervalId = setInterval(() => {
            if (isRunning && currentItem && lastTickAt) {
                const timeSinceLastTick = Date.now() - lastTickAt;

                if (timeSinceLastTick > TIMEOUT) {
                    console.error(
                        "Queue processing timeout - зависла обробка черги",
                        {
                            currentItem,
                            timeSinceLastTick,
                        },
                    );

                    dispatch(
                        patchRequestQueueItem({
                            id: currentItem.id,
                            patch: { status: QueueTaskStatus.Error },
                        }),
                    );

                    setProcessing(false);

                    notification.error({
                        message: "Обробка черги зупинена",
                        description:
                            "Процес обробки завис і був автоматично зупинений",
                    });
                }
            }
        }, 60000);

        return () => clearInterval(intervalId);
    }, [currentItem, dispatch, setProcessing, isRunning, lastTickAt]);

    const processItem = useCallback(
        async (item: RequestQueueItem) => {
            const lastErrorIndex = item?.progress?.lastErrorIndex;
            const lastCompletedIndex = item?.progress?.lastCompletedIndex;

            const successStartIndex =
                lastCompletedIndex !== undefined ? lastCompletedIndex + 1 : 0;

            const startFromIndex = lastErrorIndex ?? successStartIndex;

            queue.clear();

            for (const cmd of item.commands) {
                queue.add(cmd.title, async () => {
                    const moveToFailedData = cmd.options?.moveToFailed ?? [];
                    const ignoreErrorCodes =
                        cmd.options?.ignoreErrorCodes ?? [];

                    try {
                        const isGoodLatency = await isOnline(
                            MAX_ALLOWED_LATENCY_MS,
                        );

                        if (!isGoodLatency) {
                            throw StopQueue.mark(
                                new Error(
                                    `STOP_QUEUE: high latency ${MAX_ALLOWED_LATENCY_MS}ms`,
                                ),
                            );
                        }

                        await callHandler(handlers, cmd.type, cmd.params);

                        if (cmd.type === "postTaskComplete") {
                            dispatch(
                                setTaskCompleted({
                                    taskId: item?.metadata?.taskId ?? "",
                                    marketId: item?.metadata?.marketId ?? "",
                                }),
                            );
                        }
                    } catch (error) {
                        if (StopQueue.is(error)) throw error;

                        const stopOnError = cmd.options?.stopOnError ?? true;
                        if (!stopOnError) return;

                        const { code } = getApiError(error);

                        const ignoreError = ignoreErrorCodes.some(
                            (errorCode) => String(errorCode) === String(code),
                        );
                        if (ignoreError) return;

                        const moveToFailed = moveToFailedData.find(
                            (x) => String(x.code) === String(code),
                        );

                        if (moveToFailed) {
                            if (cmd.type === "postTaskComplete") {
                                dispatch(
                                    setTaskCompleted({
                                        taskId: item?.metadata?.taskId ?? "",
                                        marketId:
                                            item?.metadata?.marketId ?? "",
                                    }),
                                );
                            }

                            notification.error({ message: moveToFailed.label });
                            throw MoveToFailed.mark(error);
                        }

                        throw error;
                    }
                });
            }

            try {
                await queue.runSuccessively({
                    startFromIndex,
                    stopOnError: item?.options?.stopOnError ?? true,
                });

                return { ok: true };
            } catch (error) {
                if (StopQueue.is(error)) throw error;

                if (MoveToFailed.is(error)) {
                    return { ok: false, moveToFailed: true };
                }

                return { ok: false };
            }
        },
        [dispatch, handlers, queue],
    );

    useEffect(() => {
        if (offlineMode) return;

        if (
            requestQueue.length === 0 ||
            processingRef.current ||
            isInitialDataLoading
        ) {
            return;
        }

        setProcessing(true);

        const snapshot = requestQueue.filter(
            ({ status }) => status === QueueTaskStatus.Pending,
        );

        (async () => {
            try {
                for (let idx = 0; idx < snapshot.length; idx++) {
                    const online = await isOnline(MAX_ALLOWED_LATENCY_MS);
                    if (!online || offlineModeRef.current) {
                        displayStopWarning();

                        dispatch(stopRequestQueue());
                        break;
                    }

                    const item = snapshot[idx];

                    if (!isMountedRef.current) return;

                    setCurrentItem(item);

                    try {
                        const { ok, moveToFailed } = await processItem(item);

                        if (ok) {
                            dispatch(deleteRequestQueueItem(item.id));
                            continue;
                        }

                        if (moveToFailed) {
                            dispatch(
                                addFailedQueueItem({
                                    ...item,
                                    progress: queue.progress,
                                }),
                            );
                            dispatch(deleteRequestQueueItem(item.id));
                            continue;
                        }

                        dispatch(
                            patchRequestQueueItem({
                                id: item.id,
                                patch: { status: QueueTaskStatus.Error },
                            }),
                        );
                    } catch (e) {
                        if (StopQueue.is(e)) {
                            displayStopWarning();

                            dispatch(stopRequestQueue());
                            break;
                        }

                        throw e;
                    } finally {
                        if (isMountedRef.current) setCurrentItem(null);
                    }
                }
            } finally {
                if (isMountedRef.current) setProcessing(false);
            }
        })();
    }, [
        dispatch,
        offlineMode,
        processItem,
        queue,
        requestQueue,
        isInitialDataLoading,
        setProcessing,
    ]);
};
